import React, { useEffect } from "react"; // Added useEffect
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux"; // Added useDispatch
import { getUserScreens } from "../../store/reducer/screen/screenSlice";
import { getDdOptionForUI } from "../../store/reducer/ddOption/ddOptionSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faCar, 
  faCreditCard, 
  faScaleBalanced, 
  faFileInvoice, 
  faMoneyBill, 
  faFilterCircleDollar, 
  faBusinessTime, 
  faListAlt 
} from "@fortawesome/free-solid-svg-icons";

function Sidebar(props) {
  const location = useLocation();
  const dispatch = useDispatch();

  // 1. Get Data from Redux
  const userScreens = useSelector((state) => state.screens.screens || []);
  const { options } = useSelector((state) => state.ddOptions);

  // 2. Fetch data if it's missing (Handling the "empty" state)
  useEffect(() => {
    const userSession = JSON.parse(localStorage.getItem("userSession"));
    
    if (userSession?.id) {
      if (!userScreens || userScreens.length === 0) {
        dispatch(getUserScreens(userSession.id));
      }
      if (!options || options.length === 0) {
        dispatch(getDdOptionForUI());
      }
    }
  }, [dispatch, userScreens.length, options.length]);

  // 3. Permission Helper
  const hasAccess = (screenCode) => {
    if (!options || options.length === 0 || !userScreens || userScreens.length === 0) {
      return false; 
    }
    const targetOption = options.find(
      (opt) => opt.field_value === screenCode && opt.ui_name === "Screens"
    );

    return targetOption && userScreens.includes(targetOption.id);
  };

  return (
    <div
      className={`sidebar bg-[#17171B] fixed h-[100vh] flex flex-col items-center transition-width duration-500 ${
        props.isSidebarOpen ? "w-[280px] md:w-[280px]" : "w-[0px] md:w-[80px]"
      }`}
    >
      <div className="mt-[35px] mb-[70px]">
        {props.isSidebarOpen ? (
          <img
            src={process.env.PUBLIC_URL + "/image/logo.png"}
            alt="logo"
            className="w-[140px]"
          />
        ) : (
          <img
            src={process.env.PUBLIC_URL + "/image/favicon.png"}
            alt="logo"
            className="w-[50px]"
          />
        )}
      </div>
      <div className="overflow-y-scroll scrollbar-hide">
        <ul className="flex flex-col gap-[10px]">
          
          {/* DASHBOARD - Always Visible or use a specific code */}
          <li
            className={`px-[16px] py-[10px] rounded-[12px] ${
              location.pathname.startsWith("/dashboard")
                ? "text-black bg-white"
                : "text-[#747475] hover:bg-gray-200 hover:text-black"
            }`}
          >
            <Link to="/dashboard">
              <div className="flex flex-row gap-[12px]">
                <img
                  src={process.env.PUBLIC_URL + "/icons/dashboard.png"}
                  className={`${
                    !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                  }`}
                  alt="dashboard icon"
                />
                <span
                  className={`text-[#747475] hover:text-black font-normal font-Inter ${
                    !props.isSidebarOpen ? "hidden" : ""
                  }`}
                >
                  Dashboard
                </span>
              </div>
            </Link>
          </li>

          {/* EXPENSES */}
          {hasAccess("EXPENSE_LIST_VIEW") && (
            <li
              className={`px-[16px] py-[10px] rounded-[12px] ${
                location.pathname.startsWith("/expense")
                  ? "text-black bg-white"
                  : "text-[#747475] hover:bg-gray-200 hover:text-black"
              }`}
            >
              <Link to="/expenses">
                <div className="flex flex-row gap-[12px]">
                  <img
                    src={process.env.PUBLIC_URL + "/icons/expenses.png"}
                    className={`${
                      !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                    }`}
                    alt="expenses icon"
                  />
                  <span
                    className={`text-[#747475] hover:text-black font-normal font-Inter ${
                      !props.isSidebarOpen ? "hidden" : ""
                    }`}
                  >
                    Expenses
                  </span>
                </div>
              </Link>
            </li>
          )}

          {/* MILEAGE */}
          {hasAccess("MILEAGE_DASHBOARD") && (
            <li
              className={`px-[16px] py-[10px] rounded-[12px] ${
                location.pathname.startsWith("/business-mileage") || location.pathname.startsWith("/mileage")
                  ? "text-black bg-white"
                  : "text-[#747475] hover:bg-gray-200 hover:text-black"
              }`}
            >
              <Link to="/mileage">
                <div className="flex flex-row gap-[12px]">
                  <FontAwesomeIcon
                    icon={faCar}
                    className={`${
                      !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                    }`}
                    alt="mileage icon"
                  />
                  <span
                    className={`text-[#747475] hover:text-black font-normal font-Inter ${
                      !props.isSidebarOpen ? "hidden" : ""
                    }`}
                  >
                    Mileage
                  </span>
                </div>
              </Link>
            </li>
          )}

          {/* TIME ENTRY */}
          {hasAccess("TIME_ENTRY_LIST") && (
            <li
              className={`px-[16px] py-[10px] rounded-[12px] ${
                location.pathname.startsWith("/time-entries")
                  ? "text-black bg-white"
                  : "text-[#747475] hover:bg-gray-200 hover:text-black"
              }`}
            >
              <Link to="/time-entries">
                <div className="flex flex-row gap-[12px]">
                  <FontAwesomeIcon
                    icon={faBusinessTime}
                    className={`${
                      !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                    }`}
                    alt="time entry icon"
                  />
                  <span
                    className={`text-[#747475] hover:text-black font-normal font-Inter ${
                      !props.isSidebarOpen ? "hidden" : ""
                    }`}
                  >
                    Time Entry
                  </span>
                </div>
              </Link>
            </li>
          )}

          {/* CUSTOMERS */}
          {hasAccess("CUSTOMER_LIST_VIEW") && (
            <li
              className={`px-[16px] py-[10px] rounded-[12px] ${
                location.pathname.startsWith("/customer")
                  ? "text-black bg-white"
                  : "text-[#747475] hover:bg-gray-200 hover:text-black"
              }`}
            >
              <Link to="/customers">
                <div className="flex flex-row gap-[12px]">
                  <img
                    src={process.env.PUBLIC_URL + "/icons/customers.png"}
                    className={`${
                      !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                    }`}
                    alt="customers icon"
                  />
                  <span
                    className={`text-[#747475] hover:text-black font-normal font-Inter ${
                      !props.isSidebarOpen ? "hidden" : ""
                    }`}
                  >
                    Customers
                  </span>
                </div>
              </Link>
            </li>
          )}

          {/* OPPORTUNITY */}
          {hasAccess("OPPORTUNITY_LIST_VIEW") && (
            <li
              className={`px-[16px] py-[10px] rounded-[12px] ${
                location.pathname.startsWith("/opportunity") && !location.pathname.includes("summary")
                  ? "text-black bg-white"
                  : "text-[#747475] hover:bg-gray-200 hover:text-black"
              }`}
            >
              <Link to="/opportunity">
                <div className="flex flex-row gap-[12px]">
                  <FontAwesomeIcon
                    icon={faFilterCircleDollar}
                    className={`${
                      !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                    }`}
                    alt="opportunity icon"
                  />
                  <span
                    className={`text-[#747475] hover:text-black font-normal font-Inter ${
                      !props.isSidebarOpen ? "hidden" : ""
                    }`}
                  >
                    Opportunity
                  </span>
                </div>
              </Link>
            </li>
          )}
          

          {/* DRAWINGS */}
          {hasAccess("DRAWING_LIST_VIEW") && (
            <li
              className={`px-[16px] py-[10px] rounded-[12px] ${
                location.pathname.startsWith("/drawings")
                  ? "text-black bg-white"
                  : "text-[#747475] hover:bg-gray-200 hover:text-black"
              }`}
            >
              <Link to="/drawings">
                <div className="flex flex-row gap-[12px]">
                  <img
                    src={process.env.PUBLIC_URL + "/icons/drawings.png"}
                    className={`${
                      !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                    }`}
                    alt="drawings icon"
                  />
                  <span
                    className={`text-[#747475] hover:text-black font-normal font-Inter ${
                      !props.isSidebarOpen ? "hidden" : ""
                    }`}
                  >
                    Drawings
                  </span>
                </div>
              </Link>
            </li>
          )}

          {/* JOBS - Note: No screenCode provided in App.js for this yet. 
             If you have one, replace 'true' with hasAccess('JOB_LIST_VIEW') */}
          <li className="hover:bg-white px-[16px] py-[10px] hover:rounded-[12px]">
            <Link to="">
              <div className="flex flex-row gap-[12px]">
                <img
                  src={process.env.PUBLIC_URL + "/icons/jobs.png"}
                  className={`${
                    !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                  }`}
                  alt="jobs icon"
                />
                <span
                  className={`text-[#747475] hover:text-black font-normal font-Inter ${
                    !props.isSidebarOpen ? "hidden" : ""
                  }`}
                >
                  Jobs
                </span>
              </div>
            </Link>
          </li>

          {/* BANK TRANSACTIONS */}
          {hasAccess("FINANCE_BANK_VIEW") && (
            <li
              className={`px-[16px] py-[10px] rounded-[12px] ${
                location.pathname.startsWith("/bank-transactions")
                  ? "text-black bg-white"
                  : "text-[#747475] hover:bg-gray-200 hover:text-black"
              }`}
            >
              <Link to="/bank-transactions">
                <div className="flex flex-row gap-[12px]">
                  <FontAwesomeIcon
                    icon={faCreditCard}
                    className={`${
                      !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                    }`}
                    alt="bank transaction icon"
                  />
                  <span
                    className={`text-[#747475] hover:text-black font-normal font-Inter ${
                      !props.isSidebarOpen ? "hidden" : ""
                    }`}
                  >
                    Bank Transactions
                  </span>
                </div>
              </Link>
            </li>
          )}

          {/* INVOICES */}
          {hasAccess("FINANCE_INVOICE_LIST") && (
            <li
              className={`px-[16px] py-[10px] rounded-[12px] ${
                location.pathname.startsWith("/invoices")
                  ? "text-black bg-white"
                  : "text-[#747475] hover:bg-gray-200 hover:text-black"
              }`}
            >
              <Link to="/invoices">
                <div className="flex flex-row gap-[12px]">
                  <FontAwesomeIcon
                    icon={faFileInvoice}
                    className={`${
                      !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                    }`}
                    alt="invoices icon"
                  />
                  <span
                    className={`text-[#747475] hover:text-black font-normal font-Inter ${
                      !props.isSidebarOpen ? "hidden" : ""
                    }`}
                  >
                    Invoices
                  </span>
                </div>
              </Link>
            </li>
          )}

          {/* BILLS */}
          {hasAccess("FINANCE_BILL_LIST") && (
            <li
              className={`px-[16px] py-[10px] rounded-[12px] ${
                location.pathname.startsWith("/bills")
                  ? "text-black bg-white"
                  : "text-[#747475] hover:bg-gray-200 hover:text-black"
              }`}
            >
              <Link to="/bills">
                <div className="flex flex-row gap-[12px]">
                  <FontAwesomeIcon
                    icon={faMoneyBill}
                    className={`${
                      !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                    }`}
                    alt="bills icon"
                  />
                  <span
                    className={`text-[#747475] hover:text-black font-normal font-Inter ${
                      !props.isSidebarOpen ? "hidden" : ""
                    }`}
                  >
                    Bills
                  </span>
                </div>
              </Link>
            </li>
          )}

          {/* FINANCIALS */}
          {hasAccess("FINANCE_DASHBOARD") && (
            <li
              className={`px-[16px] py-[10px] rounded-[12px] ${
                location.pathname.startsWith("/financials")
                  ? "text-black bg-white"
                  : "text-[#747475] hover:bg-gray-200 hover:text-black"
              }`}
            >
              <Link to="/financials">
                <div className="flex flex-row gap-[12px]">
                  <FontAwesomeIcon
                    icon={faScaleBalanced}
                    className={`${
                      !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                    }`}
                    alt="financial icon"
                  />
                  <span
                    className={`text-[#747475] hover:text-black font-normal font-Inter ${
                      !props.isSidebarOpen ? "hidden" : ""
                    }`}
                  >
                    Financials
                  </span>
                </div>
              </Link>
            </li>
          )}

          {/* OPPORTUNITY SUMMARY */}
          {hasAccess("OPPORTUNITY_SUMMARY") && (
            <li
              className={`px-[16px] py-[10px] rounded-[12px] ${
                location.pathname.startsWith("/opportunity-summary")
                  ? "text-black bg-white"
                  : "text-[#747475] hover:bg-gray-200 hover:text-black"
              }`}
            >
              <Link to="/opportunity-summary">
                <div className="flex flex-row gap-[12px]">
                  <FontAwesomeIcon
                    icon={faListAlt}
                    className={`${
                      !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                    }`}
                    alt="opportunity summary icon"
                  />
                  <span
                    className={`text-[#747475] hover:text-black font-normal font-Inter ${
                      !props.isSidebarOpen ? "hidden" : ""
                    }`}
                  >
                    Opportunity Summary
                  </span>
                </div>
              </Link>
            </li>
          )}

          {/* REPORTS */}
          {hasAccess("REPORT_VIEW") && (
            <li
              className={`px-[16px] py-[10px] rounded-[12px] ${
                location.pathname.startsWith("/report")
                  ? "text-black bg-white"
                  : "text-[#747475] hover:bg-gray-200 hover:text-black"
              }`}
            >
              <Link to="/reports">
                <div className="flex flex-row gap-[12px]">
                  <img
                    src={process.env.PUBLIC_URL + "/icons/reports.png"}
                    className={`${
                      !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                    }`}
                    alt="reports icon"
                  />
                  <span
                    className={`text-[#747475] hover:text-black font-normal font-Inter ${
                      !props.isSidebarOpen ? "hidden" : ""
                    }`}
                  >
                    Reports
                  </span>
                </div>
              </Link>
            </li>
          )}

          {/* SETTINGS */}
          {hasAccess("SETTING_VIEW") && (
            <li
              className={`px-[16px] py-[10px] rounded-[12px] ${
                location.pathname.startsWith("/setting")
                  ? "text-black bg-white"
                  : "text-[#747475] hover:bg-gray-200 hover:text-black"
              }`}
            >
              <Link to="/setting">
                <div className="flex flex-row gap-[12px]">
                  <img
                    src={process.env.PUBLIC_URL + "/icons/setup.png"}
                    className={`${
                      !props.isSidebarOpen ? "w-[25px]" : "w-[20px] h-[20px]"
                    }`}
                    alt="settings icon"
                  />
                  <span
                    className={`font-normal font-Inter ${
                      !props.isSidebarOpen ? "hidden" : ""
                    }`}
                  >
                    Settings
                  </span>
                </div>
              </Link>
            </li>
          )}

        </ul>
      </div>
    </div>
  );
}

export default Sidebar;